import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableRow,
    LinearProgress,
    Alert,
    Snackbar,
    Tooltip,
    IconButton,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Visibility as ViewIcon,
    DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppSearch } from '../../../shared/components/AppSearch';
import collectionService, { Collection as CollectionType } from '../../../shared/services/collectionService';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';

interface CollectionRowProps {
    collection: CollectionType;
    canDrag: boolean;
    canEdit: boolean;
    canDelete: boolean;
    onEdit: (collection: CollectionType) => void;
    onDelete: (collection: CollectionType) => void;
    onView: (collection: CollectionType) => void;
}

const CollectionRow: React.FC<CollectionRowProps> = ({
    collection,
    canDrag,
    canEdit,
    canDelete,
    onEdit,
    onDelete,
    onView,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: collection.id as string,
        disabled: !canDrag,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        backgroundColor: isDragging ? '#F9FAFB' : undefined,
    };

    return (
        <TableRow ref={setNodeRef} style={style} hover>
            <TableCell sx={{ width: '5%' }}>
                <IconButton
                    size="small"
                    disabled={!canDrag}
                    {...(canDrag ? { ...attributes, ...listeners } : {})}
                    sx={{ cursor: canDrag ? 'grab' : 'not-allowed', color: canDrag ? '#6B7280' : '#D1D5DB' }}
                >
                    <DragIndicatorIcon fontSize="small" />
                </IconButton>
            </TableCell>
            <TableCell sx={{ width: '35%' }}>
                <span className="font-medium">{collection.name}</span>
            </TableCell>
            <TableCell align="center" sx={{ width: '15%' }}>
                <span style={{
                    color: collection.isActive ? '#059669' : '#DC2626',
                    fontWeight: 'bold'
                }}>
                    {collection.isActive ? 'Active' : 'Inactive'}
                </span>
            </TableCell>
            <TableCell sx={{ width: '20%' }}>
                {collection.createdAt
                    ? new Date(collection.createdAt).toLocaleDateString()
                    : '-'
                }
            </TableCell>
            <TableCell align="right" sx={{ width: '25%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Tooltip title="View Products">
                        <IconButton
                            size="small"
                            onClick={() => onView(collection)}
                            sx={{ color: '#0EA5E9' }}
                        >
                            <ViewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {canEdit && (
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                onClick={() => onEdit(collection)}
                                sx={{ color: '#8B5CF6' }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canDelete && (
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={() => onDelete(collection)}
                                sx={{ color: '#DC2626' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );
};

export const Collection: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();

    const [allCollections, setAllCollections] = useState<CollectionType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [searchInput, setSearchInput] = useState('');

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    // Load collections on mount
    useEffect(() => {
        loadCollections();
    }, []);

    const loadCollections = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await collectionService.getCollections({
                page: 1,
                limit: 1000
            });

            const data = response.data;
            if (data && typeof data === 'object' && 'items' in data && Array.isArray(data.items)) {
                setAllCollections(data.items);
            } else if (Array.isArray(data)) {
                setAllCollections(data);
            } else {
                setAllCollections([]);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load collections');
            setAllCollections([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredCollections = useMemo(() => {
        if (!searchInput.trim()) return allCollections;
        const q = searchInput.trim().toLowerCase();
        return allCollections.filter(collection =>
            (collection.name?.toLowerCase() || '').includes(q)
        );
    }, [allCollections, searchInput]);

    const handleOpenDialog = (collection?: CollectionType) => {
        if (collection) {
            // Edit — navigate to edit page
            navigate(`/collections/edit/${collection.id}`);
        } else {
            // New — navigate to add page
            navigate('/collections/add');
        }
    };

    const handleDelete = async (collection: CollectionType) => {
        if (!collection.id) return;
        if (!window.confirm(`Are you sure you want to delete "${collection.name}"?`)) return;

        try {
            setError('');
            await collectionService.deleteCollection(collection.id);
            setSuccess('Collection deleted successfully');
            await loadCollections();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete collection');
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = allCollections.findIndex((c) => c.id === active.id);
        const newIndex = allCollections.findIndex((c) => c.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(allCollections, oldIndex, newIndex);
        setAllCollections(reordered);

        try {
            await Promise.all(
                reordered.map((collection, index) =>
                    collection.id ? collectionService.updateSortOrder(collection.id, index) : Promise.resolve()
                )
            );
            setSuccess('Collection order updated');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update collection order');
            await loadCollections();
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
    };

    const canEdit = isSuperAdmin() || hasPermission('collection.update');
    const canDelete = isSuperAdmin() || hasPermission('collection.delete');
    const canCreate = isSuperAdmin() || hasPermission('collection.add');
    const canDrag = canEdit && !searchInput.trim();

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Collections
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search collections"
                        className="w-full sm:w-64"
                        disabled={loading}
                    />
                    {canCreate && (
                        <AppButton
                            variant="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                        >
                            New Collection
                        </AppButton>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {searchInput.trim() && (
                        <Alert severity="info" className="mb-4">
                            Drag-to-reorder is disabled while searching. Clear the search to reorder collections.
                        </Alert>
                    )}
                    {loading && allCollections.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && allCollections.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : filteredCollections.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                {searchInput.trim() ? 'No collections match your search.' : 'No collections found.'}
                            </Alert>
                        </Box>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700" sx={{ width: '5%' }} />
                                        <TableCell className="!font-semibold !text-gray-700" sx={{ width: '35%' }}>Name</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="center" sx={{ width: '15%' }}>Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" sx={{ width: '20%' }}>Created At</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right" sx={{ width: '25%' }}>Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={filteredCollections.length === 0}
                                emptyColSpan={5}
                                emptyMessage={searchInput.trim() ? 'No collections match your search.' : 'No collections found.'}
                            >
                                <SortableContext
                                    items={filteredCollections.map((c) => c.id as string)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {filteredCollections.map((collection) => (
                                        <CollectionRow
                                            key={collection.id}
                                            collection={collection}
                                            canDrag={canDrag}
                                            canEdit={canEdit}
                                            canDelete={canDelete}
                                            onEdit={handleOpenDialog}
                                            onDelete={handleDelete}
                                            onView={(c) => c.id && navigate(`/collections/${c.id}/products`)}
                                        />
                                    ))}
                                </SortableContext>
                            </AppTable>
                        </DndContext>
                    )}
                </div>
            </div>

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
